/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/lib/auth.ts
 * ----------------------------------------------------------------------------
 * Login/Me tolerante a diferentes backends:
 * - Permite sobrescrever endpoints via .env:
 *   VITE_AUTH_TOKEN_URL, VITE_AUTH_REFRESH_URL, VITE_AUTH_ME_URL
 * - Se não informados, tenta candidatos comuns (SimpleJWT, dj-rest-auth, Djoser…)
 * - Normaliza resposta para { access, refresh? }
 * - Persiste o “endpoint que funcionou” para acelerar próximas chamadas.
 * - Tenta JSON e, se falhar com 400/415, tenta também form-url-encoded.
 * ========================================================================== */

import { http } from '@/lib/http'
import type { AxiosResponse } from 'axios'

export type Tokens = { access: string; refresh?: string }
export type UserMe = { id?: number; username?: string;[k: string]: any }

// -------- ENV overrides (em tempo de build pelo Vite) ------------------------
const ENV_TOKEN_URL = (import.meta.env as any)?.VITE_AUTH_TOKEN_URL as string | undefined
const ENV_REFRESH_URL = (import.meta.env as any)?.VITE_AUTH_REFRESH_URL as string | undefined
const ENV_ME_URL = (import.meta.env as any)?.VITE_AUTH_ME_URL as string | undefined

// -------- Persistência do melhor endpoint ------------------------------------
const LS_LOGIN_PATH = 'movatic.auth.login_path'
const LS_REFRESH_PATH = 'movatic.auth.refresh_path'
const LS_ME_PATH = 'movatic.auth.me_path'

function readLS(key: string): string | undefined {
    try { return localStorage.getItem(key) || undefined } catch { return undefined }
}
function writeLS(key: string, value?: string) {
    try { if (value) localStorage.setItem(key, value) } catch { /* ignore */ }
}

// -------- Candidatos conhecidos ----------------------------------------------
const TOKEN_CANDIDATES = [
    // SimpleJWT (mais comuns em primeiro lugar)
    '/auth/token/',
    '/api/token/',
    // dj-rest-auth (JWT)
    '/auth/jwt/create/',
    '/api/auth/jwt/create/',
    // dj-rest-auth (Token)
    '/auth/login/',
    '/api/auth/login/',
    // Djoser (JWT ou Token)
    '/jwt/create/',
    '/api/jwt/create/',
    '/token/login/',
    '/api/token/login/',
    '/auth/token/login/',
    '/api/auth/token/login/',
    // genéricos
    '/login/',
    '/api/login/',
]

const REFRESH_CANDIDATES = [
    '/auth/token/refresh/',
    '/api/token/refresh/',
    '/auth/jwt/refresh/',
    '/api/auth/jwt/refresh/',
]

const ME_CANDIDATES = [
    '/users/me/',
    '/api/users/me/',
    '/auth/user/',
    '/api/auth/user/',
    '/me/',
    '/api/me/',
    '/api/auth/users/me/',
]

// -------- Utils ---------------------------------------------------------------
function okLike(status: number) {
    return status >= 200 && status < 300
}
function existsLike(status: number) {
    // 400/401/403/405 = a rota existe (método ou auth inválidos)
    return okLike(status) || status === 400 || status === 401 || status === 403 || status === 405
}
function normPath(p: string) {
    if (!p) return '/'
    return p.startsWith('/') ? p : `/${p}`
}
function prependIf<T>(arr: T[], item?: T) {
    return item ? [item, ...arr.filter((x) => x !== item)] : arr.slice()
}

function buildReadableError(resOrErr: any, fallback = 'Erro de autenticação') {
    const res = (resOrErr?.response ?? resOrErr) as AxiosResponse | undefined
    const status = (res as any)?.status
    const data = (res as any)?.data
    const detail =
        (data && (data.detail || data.message || data.error)) ||
        (typeof data === 'string' ? data : null) ||
        (resOrErr?.message ?? null) ||
        null
    const err = new Error(detail ? String(detail) : `${fallback}${status ? ` (HTTP ${status})` : ''}`)
        ; (err as any).response = { status, data }
    return err
}

// Tenta POST com JSON, se der 400/415 “de parsing/content-type”, tenta também x-www-form-urlencoded
async function postLoginSmart(url: string, username: string, password: string): Promise<AxiosResponse<any>> {
    // 1) JSON (padrão)
    try {
        return await http.post(
            url,
            { username, password, email: username },
            { headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=utf-8' } }
        )
    } catch (e: any) {
        const st = e?.response?.status ?? 0
        // 2) form-url-encoded se backend não aceitar JSON (bem comum em endpoints legacy)
        if (st === 400 || st === 415 || st === 406) {
            const body = new URLSearchParams()
            body.set('username', username)
            body.set('password', password)
            // alguns aceitam "email" no lugar de username
            body.set('email', username)
            return await http.post(url, body.toString(), {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
        }
        throw e
    }
}

// tenta fazer POST em ordem até uma rota “existir”/funcionar
async function tryLoginOn(urls: string[], username: string, password: string): Promise<{ url: string; data: any }> {
    let lastErr: any = null
    for (const raw of urls) {
        const url = normPath(raw)
        try {
            const res = await postLoginSmart(url, username, password)
            if (okLike(res.status)) return { url, data: res.data }
            lastErr = res
        } catch (e: any) {
            const st = e?.response?.status ?? 0
            if (existsLike(st)) lastErr = e
            else lastErr = e
        }
    }
    throw lastErr ?? new Error('Nenhum endpoint de login respondeu')
}

function normalizeTokens(payload: any): Tokens {
    // SimpleJWT (mais comum)
    if (payload?.access) return { access: payload.access, refresh: payload?.refresh }
    // variações dj-rest-auth / Djoser / custom
    if (payload?.key) return { access: payload.key }
    if (payload?.token) return { access: payload.token }
    if (payload?.auth_token) return { access: payload.auth_token }
    if (payload?.jwt) return { access: payload.jwt }
    // respostas aninhadas (defensivo)
    if (payload?.data?.access) return { access: payload.data.access, refresh: payload.data?.refresh }
    throw new Error('Não foi possível normalizar tokens a partir da resposta do servidor.')
}

async function tryFetchMeOn(urls: string[]): Promise<{ url: string; data: any }> {
    let lastErr: any = null
    for (const raw of urls) {
        const url = normPath(raw)
        try {
            const res = await http.get(url, { headers: { Accept: 'application/json' } })
            if (okLike(res.status)) return { url, data: res.data }
            lastErr = res
        } catch (e: any) {
            const st = e?.response?.status ?? 0
            if (existsLike(st)) lastErr = e
            else lastErr = e
        }
    }
    throw lastErr ?? new Error('Nenhum endpoint /me respondeu')
}

function normalizeUser(data: any): UserMe {
    const u: UserMe = typeof data === 'object' && data ? { ...data } : {}
    if (!u.username && typeof data?.email === 'string') u.username = data.email
    return u
}

// -------- API pública ---------------------------------------------------------
export async function login(username: string, password: string): Promise<Tokens> {
    const remembered = readLS(LS_LOGIN_PATH)
    const urls = prependIf(
        [
            ...(ENV_TOKEN_URL ? [ENV_TOKEN_URL] : []),
            ...TOKEN_CANDIDATES,
        ],
        remembered
    )

    try {
        const { url, data } = await tryLoginOn(urls, username, password)
        const tokens = normalizeTokens(data)
        writeLS(LS_LOGIN_PATH, url)
        // log leve para diagnóstico
        console.log('[auth] login via', url, '→ normalizado:', Object.keys(tokens))
        return tokens
    } catch (err: any) {
        // Propaga erro com detalhe do servidor se houver (ajuda no 400 do Zebra)
        throw buildReadableError(err, 'Falha ao autenticar')
    }
}

export async function refreshToken(refresh: string): Promise<Tokens> {
    if (!refresh) throw new Error('refresh vazio')

    const remembered = readLS(LS_REFRESH_PATH)
    const urls = prependIf(
        [
            ...(ENV_REFRESH_URL ? [ENV_REFRESH_URL] : []),
            ...REFRESH_CANDIDATES,
        ],
        remembered
    )

    let lastErr: any = null
    for (const raw of urls) {
        const url = normPath(raw)
        try {
            const res = await http.post(url, { refresh }, {
                headers: { Accept: 'application/json', 'Content-Type': 'application/json; charset=utf-8' },
            })
            if (okLike(res.status)) {
                const t = normalizeTokens(res.data)
                writeLS(LS_REFRESH_PATH, url)
                console.log('[auth] refresh via', url)
                return t
            }
            lastErr = res
        } catch (e: any) {
            lastErr = e
        }
    }
    throw buildReadableError(lastErr, 'Falha no refresh')
}

export async function fetchMe(): Promise<UserMe> {
    const remembered = readLS(LS_ME_PATH)
    const urls = prependIf(
        [
            ...(ENV_ME_URL ? [ENV_ME_URL] : []),
            ...ME_CANDIDATES,
        ],
        remembered
    )

    try {
        const { url, data } = await tryFetchMeOn(urls)
        const u = normalizeUser(data)
        writeLS(LS_ME_PATH, url)
        console.log('[auth] me via', url)
        return u
    } catch (err: any) {
        throw buildReadableError(err, 'Falha ao consultar /me')
    }
}
